import React, { useState } from 'react';
import { generateKeyPackage, downloadKeyPackage } from '../utils/keyPackage';
import './KeyGenerationModal.css';

const KeyGenerationModal = ({ agentData, keyMaterial, onClose }) => {
  const [currentStep, setCurrentStep] = useState('download');
  const [copyFeedback, setCopyFeedback] = useState('');

  // Debug logging
  console.log('KeyGenerationModal received agentData:', agentData);
  console.log('KeyGenerationModal received keyMaterial:', keyMaterial);

  // Add safety checks for agentData
  if (!agentData || !keyMaterial) {
    console.error('KeyGenerationModal: Missing agentData or keyMaterial');
    return null;
  }

  const agentName = agentData.profile?.name || agentData.agentName || 'Unknown Agent';
  const agentId = agentData.agentId || 'Unknown ID';

  const handleDownloadAll = () => {
    const packageData = generateKeyPackage(agentData, keyMaterial);
    const filename = `agent-${agentId}-keys.json`;
    downloadKeyPackage(packageData, filename);
    setCurrentStep('guidance');
  };

  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${label} copied!`);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
    }
  };

  if (currentStep === 'download') {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="success-header">
            <div className="success-icon">✅</div>
            <h2>Agent Successfully Registered!</h2>
            <p style={{ 
              marginTop: '25px', 
              fontSize: '18px', 
              lineHeight: '1.5',
              fontWeight: '500'
            }}>
              Your agent <strong>{agentName}</strong> has been added to the Nekuda Registry and is ready to use.
            </p>
          </div>

          <div className="key-security-notice">
            <div className="warning-icon">⚠️</div>
            <h3>Secure Your Keys</h3>
            <p>Your cryptographic keys prove your agent's identity. Keep them safe!</p>
          </div>

          <div className="download-section">
            <button 
              className="primary-download-btn"
              onClick={handleDownloadAll}
            >
              📥 Download Complete Key Package
            </button>
            <p className="download-description">
              Includes private key, public key, integration guide, and code examples
            </p>
          </div>

          <details className="advanced-options">
            <summary>Advanced: View Individual Keys</summary>
            <div className="key-grid">
              <div className="key-item">
                <label>Private Key (keep secret)</label>
                <textarea readOnly value={keyMaterial.privateKey || ''} />
                <button onClick={() => handleCopyToClipboard(keyMaterial.privateKey, 'Private Key')}>
                  Copy
                </button>
              </div>
              <div className="key-item">
                <label>Public JWK (shareable)</label>
                <textarea readOnly value={JSON.stringify(keyMaterial.publicJWK, null, 2)} />
                <button onClick={() => handleCopyToClipboard(JSON.stringify(keyMaterial.publicJWK), 'Public JWK')}>
                  Copy
                </button>
              </div>
              <div className="key-item">
                <label>Key Thumbprint</label>
                <input readOnly value={keyMaterial.thumbprint || ''} />
                <button onClick={() => handleCopyToClipboard(keyMaterial.thumbprint, 'Thumbprint')}>
                  Copy
                </button>
              </div>
            </div>
          </details>

          {copyFeedback && (
            <div className="copy-feedback">{copyFeedback}</div>
          )}
        </div>
      </div>
    );
  }

  if (currentStep === 'guidance') {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="guidance-header">
            <h2>What's Next?</h2>
            <p>Your agent is registered. Here's how to use it:</p>
          </div>

          <div className="next-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Read the Docs</h3>
                <p>Learn how signatures work and how to integrate them: <a href="https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/" target="_blank" rel="noopener noreferrer">link to resources</a></p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Add Signature Support</h3>
                <p>Use web-bot-auth or your own code to sign HTTP requests with your agent's private key.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Verify Your Public Key</h3>
                <p>Make sure your public key is live at:</p>
                <a href={`/.well-known/http-message-signatures-directory`} target="_blank" rel="noopener noreferrer">
                  {window.location.origin}/.well-known/http-message-signatures-directory
                </a>
                <p style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                  This is what websites use to verify your agent's identity.
                </p>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button onClick={onClose} className="primary-btn">
              Done
            </button>
            <button onClick={() => setCurrentStep('download')} className="secondary-btn">
              Back to Keys
            </button>
          </div>
        </div>
      </div>
    );
  }
};

export default KeyGenerationModal; 